import { useRooms } from "@/features/room/room.queries";
import { toCapitalize } from "@/utils/text-utils";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { zodSearchValidator } from "@tanstack/router-zod-adapter";
import { Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import { RoomDateColumn } from "@/components/datatable/columns/rooms/rooms-columns";
import { DataTableRowActions } from "@/components/datatable/data-table-row-action";
import { DataTableSimple } from "@/components/datatable/data-table-simple/data-table-simple";
import CreateUpdateRoomModal from "@/components/modals/create-update-room-modal";
import DeleteRoomModal from "@/components/modals/delete-room-modal";
import { useRef, useState } from "react";

const subjectRouteSchema = z.object({
  mode: z.enum(["create", "update"]).optional(),
  room: z.number().optional(),
});

export const Route = createFileRoute("/_app/(rooms)/rooms/")({
  validateSearch: zodSearchValidator(subjectRouteSchema),
  component: Rooms,
});

function Rooms() {
  return (
    <div className="flex h-full flex-col gap-y-2 px-4 py-2">
      <RoomsTabHeader />
      <RoomsTable />
    </div>
  );
}

function RoomsTabHeader() {
  const navigate = useNavigate();
  const { t } = useTranslation(["room", "common"]);

  const createRoom = () => {
    navigate({ to: ".", search: { mode: "create" } });
  };

  return (
    <div className="flex flex-row justify-between py-4">
      <div className="flex flex-col">
        <span className="text-xl font-bold">
          {t("page_title", { format: "uppercase" })}
        </span>
        <span className="text-base font-light text-neutral-500">
          {t("page_sub_title")}
        </span>
      </div>
      <div className="flex flex-row space-x-4">
        <Button
          className="flex items-center gap-3"
          onClick={createRoom}
          size="default"
        >
          <Plus strokeWidth={4} className="h-6 w-6" />
          {toCapitalize(t("room", { ns: "common" }))}
        </Button>
      </div>
      <CreateUpdateRoomModal />
    </div>
  );
}

function RoomsTable() {
  const { t } = useTranslation(["table", "common"]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const room = useRef({ id: 0, name: "" });
  const navigate = useNavigate();
  const { data = [], isLoading } = useRooms();

  const updateRoom = (id: number) => {
    navigate({ to: ".", search: { mode: "update", room: id } });
  };

  const deleteRoom = (id: number, name: string) => {
    room.current = { id, name };
    setIsDeleteModalOpen(true);
  };

  return (
    <>
      <DataTableSimple
        data={data}
        columns={["id", "name", "createdAt", "updatedAt"]}
        isLoading={isLoading}
        columnsLabels={{
          id: t("serial_id"),
          name: toCapitalize(t("name")),
          createdAt: t("created_at"),
          updatedAt: t("updated_at"),
        }}
        customRender={{
          createdAt: (row) => (
            <RoomDateColumn date={row.getValue("createdAt")} />
          ),
          updatedAt: (row) => (
            <RoomDateColumn date={row.getValue("updatedAt")} />
          ),
        }}
        extraColumns={[
          {
            id: "actions",
            size: 50,
            cell: ({ row }) => (
              <DataTableRowActions
                row={row}
                menuItems={[
                  {
                    label: toCapitalize(t("edit", { ns: "common" })),
                    onClick: () => updateRoom(row.getValue("id")),
                    isLoading: false,
                  },
                  {
                    label: toCapitalize(t("delete", { ns: "common" })),
                    onClick: () =>
                      deleteRoom(row.getValue("id"), row.getValue("name")),
                    isLoading: false,
                  },
                ]}
              />
            ),
          },
        ]}
        className="flex h-full flex-col overflow-y-auto"
      />
      <DeleteRoomModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        room={room.current}
      />
    </>
  );
}
